import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, CalendarCheck, CurrencyEur, Clock, Check, X, ArrowRight, CaretDown, SignOut, Car, UserPlus, Link as LinkIcon, Copy, PencilSimple, Trash, DownloadSimple } from "@phosphor-icons/react";
import { useConnectAuth } from "./ConnectAuthContext";
import { toast } from "sonner";
import axios from "axios";
import AdminPropertyTab from "./AdminPropertyTab";

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem("altura_connect_token");
const ax = axios.create({ baseURL: `${API}/api/connect` });
ax.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_LABELS = {
  draft: "Brouillon", pending: "En attente", active: "Actif", suspended: "Suspendu", refused: "Refusé",
  pending_payment: "Attente paiement", paid: "Payé", confirmed: "Confirmée",
  driver_assigned: "Chauffeur assigné", en_route: "En route", in_progress: "En cours",
  completed: "Effectuée", cancelled: "Annulée",
};

const STATUS_COLORS = {
  draft: "bg-gray-200 text-gray-700", pending: "bg-yellow-100 text-yellow-800", active: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-800", refused: "bg-red-100 text-red-800",
  pending_payment: "bg-yellow-100 text-yellow-800", paid: "bg-blue-100 text-blue-800",
  confirmed: "bg-green-100 text-green-800", driver_assigned: "bg-indigo-100 text-indigo-800",
  completed: "bg-green-200 text-green-900", cancelled: "bg-red-100 text-red-800",
};

const Badge = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${STATUS_COLORS[status] || "bg-gray-100 text-gray-800"}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const VEHICLE_NAMES = { classe_e: "Classe E", classe_v: "Classe V", classe_s: "Classe S" };
const COMM_STATUS_LABELS = { pending: "En attente", available: "Disponible", paid: "Payé", cancelled: "Annulé" };
const COMM_STATUS_COLORS = { pending: "bg-yellow-100 text-yellow-800", available: "bg-blue-100 text-blue-800", paid: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" };
const PAYMENT_METHOD_LABELS = { online: "Paiement en ligne", onboard: "Règlement à bord" };
const REFUND_LABELS = { refunded: "Remboursée", not_refunded: "Non remboursée", partial: "Remboursement partiel" };

const AdminCommissions = ({ ax, load, commBatches, payCommissions, toast }) => {
  const [allBookings, setAllBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editPrice, setEditPrice] = useState("");

  useEffect(() => {
    ax.get("/admin/commissions/history").then(({ data }) => setAllBookings(data)).catch(() => {});
  }, [ax]);

  const reload = () => {
    ax.get("/admin/commissions/history").then(({ data }) => setAllBookings(data)).catch(() => {});
    load();
  };

  const updateCommission = async (bookingId, updates) => {
    try {
      await ax.post(`/admin/bookings/${bookingId}/commission`, updates);
      toast.success("Mis à jour");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const filtered = allBookings.filter(b => {
    if (filter === "all") return true;
    return b.commission_status === filter;
  });

  const totals = {
    pending: allBookings.filter(b => b.commission_status === "pending").reduce((s, b) => s + (b.commission_amount || 0), 0),
    available: allBookings.filter(b => b.commission_status === "available").reduce((s, b) => s + (b.commission_amount || 0), 0),
    paid: allBookings.filter(b => b.commission_status === "paid").reduce((s, b) => s + (b.commission_amount || 0), 0),
  };

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "En attente", value: totals.pending, color: "text-yellow-700" },
          { label: "Disponible (mardi)", value: totals.available, color: "text-blue-700" },
          { label: "Payé", value: totals.paid, color: "text-green-700" },
          { label: "Total dû", value: totals.pending + totals.available, color: "text-[#1A1A1A]" },
        ].map(c => (
          <div key={c.label} className="bg-white border border-[#1A1A1A]/10 p-4 text-center">
            <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">{c.label}</p>
            <p className={`font-heading text-xl ${c.color}`}>{c.value.toFixed(2)}€</p>
          </div>
        ))}
      </div>

      {/* Batch pay (available commissions grouped by partner) */}
      {commBatches.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-5 mb-6">
          <p className="text-blue-800 text-sm font-medium mb-3">Versements du mardi — {commBatches.length} partenaire(s)</p>
          {commBatches.map(batch => (
            <div key={batch._id} className="flex items-center justify-between py-2 border-b border-blue-100 last:border-0">
              <div>
                <p className="text-blue-900 text-sm">{batch.partner_name} <span className="text-blue-500 text-xs">({batch.booking_count} courses)</span></p>
                <p className="text-blue-400 text-xs">{batch.partner_email}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-heading text-lg text-blue-900">{batch.total_due.toFixed(2)}€</span>
                <button onClick={() => payCommissions(batch._id)} className="text-xs px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 transition-colors" data-testid={`pay-batch-${batch._id}`}>
                  Marquer payé
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Filtrer :</span>
        {["all", "pending", "available", "paid", "cancelled"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 border transition-colors ${filter === f ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]/60 hover:border-[#1A1A1A]/40"}`}>
            {f === "all" ? "Tout" : COMM_STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Bookings list */}
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-[#1A1A1A]/50 text-sm py-4">Aucune commission</p>}
        {filtered.map(b => (
          <div key={b.id} className="bg-white border border-[#1A1A1A]/10 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div>
                <p className="text-[#1A1A1A] text-sm font-medium">{b.client_name} <span className="text-[#1A1A1A]/40 text-xs">({b.partner_name})</span></p>
                <p className="text-[#1A1A1A]/50 text-xs">{b.pickup_date} · {b.pickup_address} → {b.dropoff_address || "—"}</p>
              </div>
              <div className="text-right">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${COMM_STATUS_COLORS[b.commission_status] || "bg-gray-100"}`}>
                  {COMM_STATUS_LABELS[b.commission_status] || b.commission_status}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-[#1A1A1A]/60 mb-3">
              <span>Prix client: <strong className="text-[#1A1A1A]">{b.total_amount}€</strong></span>
              {b.base_price && b.base_price !== b.total_amount && <span>Base: {b.base_price}€</span>}
              <span>Commission: <strong className="text-green-700">{b.commission_amount}€</strong></span>
              {b.commission_override && <span className="text-amber-600">(modifié manuellement)</span>}
              {b.commission_paid_at && <span>Payé le {b.commission_paid_at.substring(0, 10)}</span>}
            </div>

            {/* Edit controls */}
            {editingId === b.id ? (
              <div className="flex flex-wrap items-end gap-3 bg-[#EDEBE4] p-3 border border-[#1A1A1A]/10">
                <div>
                  <label className="text-[#1A1A1A]/50 text-xs block mb-1">Prix client</label>
                  <input type="number" step="0.01" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="input-luxury bg-white text-xs w-24" />
                </div>
                <div>
                  <label className="text-[#1A1A1A]/50 text-xs block mb-1">Commission</label>
                  <input type="number" step="0.01" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} className="input-luxury bg-white text-xs w-24" />
                </div>
                <button onClick={() => {
                  const updates = {};
                  if (editPrice) updates.total_amount = parseFloat(editPrice);
                  if (editAmount) updates.commission_amount = parseFloat(editAmount);
                  updateCommission(b.id, updates);
                  setEditingId(null);
                }} className="text-xs px-3 py-2 bg-[#1A1A1A] text-white">Sauvegarder</button>
                <button onClick={() => setEditingId(null)} className="text-xs px-3 py-2 border border-[#1A1A1A]/20">Annuler</button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { setEditingId(b.id); setEditAmount(String(b.commission_amount || 0)); setEditPrice(String(b.total_amount || 0)); }}
                  className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors">Modifier</button>
                {b.commission_status === "pending" && (
                  <button onClick={() => updateCommission(b.id, { commission_status: "available" })}
                    className="text-xs px-3 py-1.5 border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors">Rendre disponible</button>
                )}
                {b.commission_status === "available" && (
                  <button onClick={() => updateCommission(b.id, { commission_status: "paid" })}
                    className="text-xs px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 transition-colors">Marquer payé</button>
                )}
                {b.commission_status === "paid" && (
                  <button onClick={() => updateCommission(b.id, { commission_status: "available" })}
                    className="text-xs px-3 py-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors">Remettre disponible</button>
                )}
                {b.commission_status !== "cancelled" && (
                  <button onClick={() => updateCommission(b.id, { commission_status: "cancelled" })}
                    className="text-xs px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors">Annuler</button>
                )}
                {b.commission_status === "cancelled" && (
                  <button onClick={() => updateCommission(b.id, { commission_status: "pending" })}
                    className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors">Rétablir</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useConnectAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [commBatches, setCommBatches] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelRefundStatus, setCancelRefundStatus] = useState("not_refunded");
  const [cancelRefundAmount, setCancelRefundAmount] = useState("");
  const [allBookings, setAllBookings] = useState(null);
  const [allBookingsFilter, setAllBookingsFilter] = useState("all");
  const [propertyStats, setPropertyStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p, b, d, c, ab] = await Promise.all([
        ax.get("/admin/dashboard"),
        ax.get("/admin/partners"),
        ax.get("/admin/bookings"),
        ax.get("/admin/drivers"),
        ax.get("/admin/commissions/pending"),
        ax.get("/admin/all-bookings"),
      ]);
      setStats(s.data);
      setPartners(p.data);
      setBookings(b.data);
      setDrivers(d.data);
      setCommBatches(c.data);
      setAllBookings(ab.data);
      // Load property stats
      try {
        const ps = await axios.get(`${API}/api/property/admin/stats`, { headers: { Authorization: `Bearer ${getToken()}` } });
        setPropertyStats(ps.data);
      } catch { /* silent */ }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/connect");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const partnerAction = async (id, action) => {
    await ax.post(`/admin/partners/${id}/${action}`);
    load();
  };

  const updateBookingStatus = async (id, status) => {
    if (status === "cancelled") {
      setCancelModal(id);
      setCancelRefundStatus("not_refunded");
      setCancelRefundAmount("");
      return;
    }
    await ax.post(`/admin/bookings/${id}/status`, { status });
    load();
  };

  const confirmCancel = async () => {
    if (!cancelModal) return;
    const payload = { status: "cancelled", refund_status: cancelRefundStatus };
    if (cancelRefundStatus === "partial" && cancelRefundAmount) {
      payload.refund_amount = parseFloat(cancelRefundAmount);
    }
    try {
      await ax.post(`/admin/bookings/${cancelModal}/status`, payload);
      toast.success("Commande annulée");
      setCancelModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const payCommissions = async (partnerId) => {
    await ax.post(`/admin/commissions/pay/${partnerId}`);
    load();
  };

  const generatePaymentLink = async (bookingId) => {
    try {
      const { data } = await ax.post(`/bookings/${bookingId}/payment-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success("Lien de paiement copié !");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const resendCredentials = async (partnerId) => {
    try {
      await ax.post(`/admin/partners/${partnerId}/resend-credentials`);
      toast.success("Email avec les accès renvoyé !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const openEditPartner = (p) => {
    setEditingPartner(p._id);
    setEditForm({
      email: p.email || "",
      full_name: p.full_name || "",
      company_name: p.company_name || "",
      phone: p.phone || "",
      position: p.position || "",
      company_type: p.company_type || "",
      city: p.city || "",
      notes_admin: p.notes_admin || "",
    });
  };

  const saveEditPartner = async () => {
    setEditSaving(true);
    try {
      await ax.put(`/admin/partners/${editingPartner}`, editForm);
      toast.success("Partenaire mis à jour");
      setEditingPartner(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setEditSaving(false); }
  };

  const deletePartner = async (partnerId) => {
    try {
      const { data } = await ax.delete(`/admin/partners/${partnerId}`);
      toast.success(data.message || "Partenaire supprimé");
      setDeleteConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleLogout = async () => { await logout(); navigate("/connect"); };

  const tabs = [
    { id: "dashboard", label: "Vue d'ensemble" },
    { id: "all", label: "Toutes les courses" },
    { id: "bookings", label: "Courses Connect" },
    { id: "property", label: "Property Services" },
    { id: "partners", label: "Partenaires" },
    { id: "commissions", label: "Commissions" },
    { id: "drivers", label: "Chauffeurs" },
  ];

  return (
    <main className="min-h-screen bg-[#EDEBE4]" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-heading text-sm tracking-[0.25em] uppercase">Altura Connect</span>
            <span className="text-white/40 text-xs">Admin</span>
          </div>
          <button onClick={handleLogout} className="text-white/60 hover:text-white text-xs flex items-center gap-2" data-testid="admin-logout">
            <SignOut size={14} /> Déconnexion
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 flex gap-0 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`py-3 px-5 text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-[#1A1A1A] text-[#1A1A1A] font-medium" : "border-transparent text-[#1A1A1A]/50 hover:text-[#1A1A1A]/80"}`}
              data-testid={`admin-tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* DASHBOARD */}
        {tab === "dashboard" && stats && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: "Réservations", value: stats.total_bookings, icon: CalendarCheck },
                { label: "Partenaires actifs", value: stats.active_partners, icon: Users },
                { label: "Demandes en attente", value: stats.pending_partners, icon: UserPlus },
                { label: "CA total", value: `${stats.total_ca}€`, icon: CurrencyEur },
                { label: "Commissions en attente", value: `${stats.commissions_pending}€`, icon: Clock },
                { label: "Commissions disponibles", value: `${stats.commissions_available}€`, icon: Check },
              ].map(s => (
                <div key={s.label} className="bg-white border border-[#1A1A1A]/10 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <s.icon size={18} weight="light" className="text-[#1A1A1A]/50" />
                    <p className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide">{s.label}</p>
                  </div>
                  <p className="text-[#1A1A1A] font-heading text-2xl">{s.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-6">
              {propertyStats && (
                <div className="mb-6">
                  <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-3">Property Services</p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-[#1A1A1A] text-white p-4">
                      <p className="text-white/50 text-xs uppercase tracking-wide mb-1">En attente</p>
                      <p className="font-heading text-2xl">{propertyStats.pending}</p>
                    </div>
                    <div className="bg-white border border-[#1A1A1A]/10 p-4">
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Confirmées</p>
                      <p className="font-heading text-2xl text-[#1A1A1A]">{propertyStats.confirmed}</p>
                    </div>
                    <div className="bg-white border border-[#1A1A1A]/10 p-4">
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Effectuées ce mois</p>
                      <p className="font-heading text-2xl text-[#1A1A1A]">{propertyStats.completed_this_month}</p>
                    </div>
                    <div className="bg-white border border-[#1A1A1A]/10 p-4">
                      <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">CA Property</p>
                      <p className="font-heading text-2xl text-[#1A1A1A]">{propertyStats.total_ca}€</p>
                    </div>
                  </div>
                </div>
              )}
              <button
                data-testid="admin-test-email-btn"
                onClick={async () => {
                  try {
                    const { data } = await ax.post("/admin/test-email");
                    if (data.success) toast.success(`Email test envoyé à ${data.admin_email}`);
                    else toast.error(`Échec: ${data.error || "erreur inconnue"}`);
                  } catch { toast.error("Erreur lors du test email"); }
                }}
                className="px-4 py-2 text-xs bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors tracking-wide uppercase"
              >
                Tester Email Admin
              </button>
            </div>
          </div>
        )}

        {/* BOOKINGS */}
        {tab === "bookings" && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">Réservations Connect</h2>

            {/* Cancel Modal */}
            {cancelModal && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-red-50 border-2 border-red-300 p-5 mb-4" data-testid="cancel-modal">
                <p className="text-red-800 text-sm font-medium mb-3">Annuler la commande — Choisir le statut de remboursement</p>
                <div className="grid sm:grid-cols-3 gap-2 mb-4">
                  {[
                    { id: "not_refunded", label: "Non remboursée" },
                    { id: "refunded", label: "Remboursée" },
                    { id: "partial", label: "Remboursement partiel" },
                  ].map(opt => (
                    <button key={opt.id} type="button" onClick={() => setCancelRefundStatus(opt.id)}
                      className={`p-3 border text-left text-xs transition-all ${cancelRefundStatus === opt.id ? "border-red-500 bg-red-100 text-red-800" : "border-red-200 text-red-600 hover:bg-red-50"}`}
                      data-testid={`cancel-${opt.id}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {cancelRefundStatus === "partial" && (
                  <div className="mb-4">
                    <label className="text-red-700 text-xs block mb-1">Montant remboursé (€)</label>
                    <input type="number" step="0.01" value={cancelRefundAmount} onChange={(e) => setCancelRefundAmount(e.target.value)}
                      className="input-luxury bg-white text-sm w-40" placeholder="0.00" data-testid="cancel-refund-amount" />
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={confirmCancel} className="text-xs px-5 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors" data-testid="cancel-confirm-btn">
                    Confirmer l{"'"}annulation
                  </button>
                  <button onClick={() => setCancelModal(null)} className="text-xs px-5 py-2 border border-red-300 text-red-600">Retour</button>
                </div>
              </motion.div>
            )}

            {bookings.length === 0 && <p className="text-[#1A1A1A]/50 text-sm">Aucune réservation</p>}
            {bookings.map(b => (
              <div key={b.id} className="bg-white border border-[#1A1A1A]/10 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[#1A1A1A] font-medium text-sm">{b.client_name}</p>
                    <p className="text-[#1A1A1A]/50 text-xs">{b.partner_name} · {b.pickup_date} à {b.pickup_time}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge status={b.status} />
                    <span className="text-[#1A1A1A] font-heading text-lg">{b.total_amount}€</span>
                  </div>
                </div>
                <div className="text-xs text-[#1A1A1A]/60 space-y-1 mb-3">
                  <p>Pick-up : {b.pickup_address}</p>
                  <p>Drop off : {b.dropoff_address}</p>
                  <p>{VEHICLE_NAMES[b.vehicle_type] || b.vehicle_type} · {b.passengers} pax · {b.luggage} bagages</p>
                  {b.payment_method && <p className="text-[#1A1A1A]/40">{PAYMENT_METHOD_LABELS[b.payment_method] || b.payment_method}</p>}
                  {b.flight_train_number && <p>Vol/Train : {b.flight_train_number}</p>}
                  {b.notes && <p>Notes : {b.notes}</p>}
                  {b.internal_notes && <p className="text-amber-700">Commentaires internes : {b.internal_notes}</p>}
                  {b.ordered_by && <p className="text-[#1A1A1A]/40">Commandé par : {b.ordered_by}</p>}
                  {b.extra_stops && b.extra_stops.length > 0 && (
                    <div className="pl-3 border-l-2 border-[#1A1A1A]/10 mt-1">
                      <p className="text-[#1A1A1A]/50">Arrêt{b.extra_stops.length > 1 ? "s" : ""} sup. : {b.extra_stops.map(s => s.name || s.address).join(", ")} (+{b.extra_stops_total || 0}€)</p>
                    </div>
                  )}
                  {b.driver && <p className="text-indigo-600">Chauffeur : {b.driver.name} · {b.driver.vehicle_model} · {b.driver.plate}</p>}
                  <p>Commission : {b.commission_amount}€ ({b.zone})</p>
                  {b.status === "cancelled" && b.refund_status && (
                    <p className="text-red-600">{REFUND_LABELS[b.refund_status] || b.refund_status}{b.refund_amount > 0 ? ` (${b.refund_amount}€)` : ""}</p>
                  )}
                  <p className={`text-[10px] ${b.admin_notified ? "text-green-600" : "text-orange-500"}`}>
                    Email admin : {b.admin_notified ? "Envoye" : "Non envoye"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {b.status === "pending_payment" && (
                    <button onClick={() => generatePaymentLink(b.id)} className="text-xs px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1.5" data-testid={`admin-gen-link-${b.id}`}>
                      <LinkIcon size={12} weight="bold" /> Lien paiement
                    </button>
                  )}
                  {b.payment_link && b.status === "pending_payment" && (
                    <button onClick={() => { navigator.clipboard.writeText(b.payment_link); toast.success("Lien copié !"); }} className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#1A1A1A]/5 transition-colors flex items-center gap-1.5">
                      <Copy size={12} /> Copier lien
                    </button>
                  )}
                  <button onClick={() => { window.open(`${API}/api/connect/bookings/${b.id}/pdf`, "_blank"); }}
                    className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors flex items-center gap-1.5" data-testid={`admin-pdf-${b.id}`}>
                    <DownloadSimple size={12} /> PDF
                  </button>
                  <button onClick={async () => {
                    try {
                      const { data } = await ax.post(`/admin/bookings/${b.id}/resend-notification`);
                      toast.success(data.success ? `Notification renvoyee a ${data.admin_email}` : "Echec de l'envoi");
                    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                  }} className="text-xs px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1.5" data-testid={`admin-resend-${b.id}`}>
                    Renvoyer email
                  </button>
                  {["confirmed", "driver_assigned", "in_progress", "completed", "cancelled"].map(s => (
                    b.status !== s && <button key={s} onClick={() => updateBookingStatus(b.id, s)}
                      className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#1A1A1A] hover:text-white transition-colors"
                      data-testid={`booking-${b.id}-${s}`}
                    >{STATUS_LABELS[s]}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PARTNERS */}
        {tab === "partners" && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">Partenaires</h2>

            {/* Edit Modal */}
            {editingPartner && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border-2 border-[#1A1A1A] p-6 mb-4" data-testid="edit-partner-modal">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-heading text-lg text-[#1A1A1A]">Modifier le partenaire</h3>
                  <button onClick={() => setEditingPartner(null)} className="text-[#1A1A1A]/50 hover:text-[#1A1A1A]"><X size={18} /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  {[
                    { key: "email", label: "Email", type: "email" },
                    { key: "full_name", label: "Nom complet" },
                    { key: "company_name", label: "Société" },
                    { key: "phone", label: "Téléphone" },
                    { key: "position", label: "Poste" },
                    { key: "company_type", label: "Type" },
                    { key: "city", label: "Ville" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-1">{f.label}</label>
                      <input type={f.type || "text"} value={editForm[f.key] || ""} onChange={(e) => setEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        className="input-luxury w-full bg-transparent text-sm" data-testid={`edit-partner-${f.key}`} />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-1">Notes admin</label>
                    <textarea value={editForm.notes_admin || ""} onChange={(e) => setEditForm(prev => ({ ...prev, notes_admin: e.target.value }))}
                      rows={2} className="input-luxury w-full bg-transparent text-sm resize-none" data-testid="edit-partner-notes" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEditPartner} disabled={editSaving}
                    className="text-xs px-5 py-2 bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors disabled:opacity-50" data-testid="edit-partner-save">
                    {editSaving ? "Enregistrement..." : "Sauvegarder"}
                  </button>
                  <button onClick={() => setEditingPartner(null)} className="text-xs px-5 py-2 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors">Annuler</button>
                </div>
              </motion.div>
            )}

            {/* Delete Confirmation */}
            {deleteConfirm && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-red-50 border-2 border-red-300 p-5 mb-4" data-testid="delete-partner-confirm">
                <p className="text-red-800 text-sm font-medium mb-1">Confirmer la suppression</p>
                <p className="text-red-700 text-xs mb-4">
                  Supprimer <strong>{partners.find(p => p._id === deleteConfirm)?.company_name}</strong> et toutes ses réservations ? Cette action est irréversible.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => deletePartner(deleteConfirm)}
                    className="text-xs px-5 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors" data-testid="delete-partner-yes">
                    Oui, supprimer définitivement
                  </button>
                  <button onClick={() => setDeleteConfirm(null)}
                    className="text-xs px-5 py-2 border border-red-300 text-red-600 hover:bg-red-50 transition-colors">Annuler</button>
                </div>
              </motion.div>
            )}

            {partners.map(p => (
              <div key={p._id} className="bg-white border border-[#1A1A1A]/10 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-[#1A1A1A] font-medium">{p.company_name}</p>
                    <p className="text-[#1A1A1A]/50 text-xs">{p.full_name} · {p.position} · {p.email}</p>
                    <p className="text-[#1A1A1A]/40 text-xs">{p.company_type} · {p.city}, {p.country}</p>
                    {p.phone && <p className="text-[#1A1A1A]/40 text-xs">{p.phone}</p>}
                  </div>
                  <Badge status={p.status} />
                </div>
                {p.stats && (
                  <p className="text-xs text-[#1A1A1A]/50 mb-2">
                    {p.stats.bookings} réservations · CA: {p.stats.ca}€ · Commissions: {p.stats.commissions}€
                  </p>
                )}
                {/* Payout preference display */}
                <div className="text-xs mb-3 bg-[#EDEBE4]/50 p-3 border border-[#1A1A1A]/5" data-testid={`partner-payout-${p._id}`}>
                  <p className="text-[#1A1A1A]/50 text-[10px] uppercase tracking-wider mb-1">Mode de versement</p>
                  {p.payout_method === "iban" && (
                    <div>
                      <span className="text-blue-700 font-medium">Virement IBAN</span>
                      <p className="text-[#1A1A1A]/60 mt-1">Titulaire : {p.iban_holder || "—"}</p>
                      <p className="text-[#1A1A1A]/60">IBAN : {p.iban || "—"}</p>
                      {p.iban_bic && <p className="text-[#1A1A1A]/60">BIC : {p.iban_bic}</p>}
                    </div>
                  )}
                  {p.payout_method === "cash" && <span className="text-amber-700 font-medium">Remise en main propre</span>}
                  {(!p.payout_method || p.payout_method === "none") && <span className="text-[#1A1A1A]/40">Non configuré par le partenaire</span>}
                </div>
                {p.notes_admin && <p className="text-xs text-[#1A1A1A]/40 italic mb-2">Note admin : {p.notes_admin}</p>}
                <div className="flex flex-wrap gap-2">
                  {/* Edit button */}
                  <button onClick={() => openEditPartner(p)}
                    className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors flex items-center gap-1.5" data-testid={`partner-edit-${p._id}`}>
                    <PencilSimple size={12} /> Modifier
                  </button>
                  {p.status === "pending" && <>
                    <button onClick={() => partnerAction(p._id, "validate")} className="text-xs px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 transition-colors" data-testid={`partner-validate-${p._id}`}>Valider</button>
                    <button onClick={() => partnerAction(p._id, "refuse")} className="text-xs px-3 py-1.5 bg-red-600 text-white hover:bg-red-700 transition-colors" data-testid={`partner-refuse-${p._id}`}>Refuser</button>
                  </>}
                  {p.status === "active" && <>
                    <button onClick={() => resendCredentials(p._id)} className="text-xs px-3 py-1.5 bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors" data-testid={`partner-resend-${p._id}`}>Renvoyer accès</button>
                    <button onClick={() => partnerAction(p._id, "suspend")} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 transition-colors">Suspendre</button>
                  </>}
                  {p.status === "suspended" && <button onClick={() => partnerAction(p._id, "reactivate")} className="text-xs px-3 py-1.5 border border-green-300 text-green-600 hover:bg-green-50 transition-colors">Réactiver</button>}
                  {/* Delete button */}
                  <button onClick={() => setDeleteConfirm(p._id)}
                    className="text-xs px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors flex items-center gap-1.5" data-testid={`partner-delete-${p._id}`}>
                    <Trash size={12} /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* COMMISSIONS */}
        {tab === "commissions" && <AdminCommissions ax={ax} load={load} commBatches={commBatches} payCommissions={payCommissions} toast={toast} />}

        {/* ALL BOOKINGS (UNIFIED) */}
        {tab === "all" && allBookings && (
          <div className="space-y-4">
            <h2 className="font-heading text-xl text-[#1A1A1A] mb-2">Toutes les réservations</h2>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white border border-[#1A1A1A]/10 p-4 text-center">
                <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Site public</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{allBookings.stats.public_count}</p>
                <p className="text-[#1A1A1A]/40 text-xs">{allBookings.stats.public_ca}€ CA</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-4 text-center">
                <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Connect</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{allBookings.stats.connect_count}</p>
                <p className="text-[#1A1A1A]/40 text-xs">{allBookings.stats.connect_ca}€ CA</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-4 text-center">
                <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Total</p>
                <p className="font-heading text-xl text-[#1A1A1A]">{allBookings.stats.total_count}</p>
                <p className="text-[#1A1A1A]/40 text-xs">{allBookings.stats.total_ca}€ CA</p>
              </div>
            </div>
            {/* Filter */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Filtrer :</span>
              {[
                { id: "all", label: "Tout" },
                { id: "public", label: "Public" },
                { id: "connect", label: "Connect" },
              ].map(f => (
                <button key={f.id} onClick={() => setAllBookingsFilter(f.id)}
                  className={`text-xs px-3 py-1.5 border transition-colors ${allBookingsFilter === f.id ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]/60 hover:border-[#1A1A1A]/40"}`}
                  data-testid={`all-filter-${f.id}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {/* Bookings list */}
            <div className="space-y-2">
              {allBookings.bookings
                .filter(b => allBookingsFilter === "all" || b.source === allBookingsFilter)
                .map(b => (
                <div key={`${b.source}-${b.id}`} className="bg-white border border-[#1A1A1A]/10 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-[#1A1A1A] font-medium text-sm">
                        {b.client_name}
                        <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded-sm ${b.source === "connect" ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {b.source === "connect" ? "Connect" : "Public"}
                        </span>
                      </p>
                      <p className="text-[#1A1A1A]/50 text-xs">
                        {b.partner_name ? `${b.partner_name} · ` : ""}{b.pickup_date} à {b.pickup_time}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge status={b.status} />
                      <p className="font-heading text-lg text-[#1A1A1A] mt-0.5">{b.total_amount}€</p>
                    </div>
                  </div>
                  <div className="text-xs text-[#1A1A1A]/60 space-y-0.5">
                    <p>Pick-up : {b.pickup_address}</p>
                    {b.dropoff_address && <p>Drop off : {b.dropoff_address}</p>}
                    <p>{VEHICLE_NAMES[b.vehicle_type] || b.vehicle_type} · {b.passengers} pax</p>
                    {b.flight_train_number && <p>Vol/Train : {b.flight_train_number}</p>}
                    {b.payment_method && <p className="text-[#1A1A1A]/40">{PAYMENT_METHOD_LABELS[b.payment_method] || b.payment_method}</p>}
                    {b.source === "connect" && b.commission_amount > 0 && <p className="text-green-700">Commission : {b.commission_amount}€</p>}
                  </div>
                </div>
              ))}
              {allBookings.bookings.filter(b => allBookingsFilter === "all" || b.source === allBookingsFilter).length === 0 && (
                <p className="text-[#1A1A1A]/50 text-sm py-4">Aucune réservation</p>
              )}
            </div>
          </div>
        )}

        {/* DRIVERS */}
        {tab === "property" && (
          <AdminPropertyTab onReload={load} />
        )}

        {/* DRIVERS */}
        {tab === "drivers" && (
          <div>
            <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">Base chauffeurs</h2>
            <div className="space-y-3">
              {drivers.map(d => (
                <div key={d.id} className="bg-white border border-[#1A1A1A]/10 p-4 flex items-center justify-between">
                  <div>
                    <p className="text-[#1A1A1A] font-medium text-sm">{d.name}</p>
                    <p className="text-[#1A1A1A]/50 text-xs">{d.phone} · {d.vehicle_model} · {d.plate}</p>
                  </div>
                  <p className="text-[#1A1A1A]/40 text-xs">{d.passenger_capacity} pax · {d.luggage_capacity} bagages</p>
                </div>
              ))}
              {drivers.length === 0 && <p className="text-[#1A1A1A]/50 text-sm">Aucun chauffeur enregistré</p>}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminDashboard;
